// apps/web/src/components/primitives/isian/enter.ts
/**
 * The one Enter rule of every form (spec 2026-09-30-sapuan-isian, 3.9), implemented once in
 * the form wrapper (FormIsian) and without React here. In a form of several fields Enter
 * moves to the next field, and on the last field it saves; in a form of one field it saves.
 * Where Enter belongs to the control it is left alone: an open list picks its item, a
 * textarea takes a new line, the tag box turns its text into a chip (it prevents the
 * default), a button or link does what it does. Grids (Mode Salin Buku, the KK grid) keep
 * their own rule and never reach this one.
 */
export interface SasaranEnter {
  /** The element's tag name, upper case ("INPUT", "SPAN"). */
  tag: string
  /** An input's type, null for other elements. */
  jenis: string | null
  /** The element's role attribute. */
  peran: string | null
  /** aria-expanded="true": a list is open under it. */
  terbuka: boolean
  /** A handler below already took the key (event.defaultPrevented). */
  dicegah: boolean
  /** Shift, Ctrl, Alt or Meta held. */
  pengubah: boolean
  /** An input method is still composing. */
  komposisi: boolean
}

export type AksiEnter = 'biarkan' | 'lanjut' | 'simpan'

const INPUT_BUKAN_ISIAN = new Set(['submit', 'button', 'reset', 'image', 'file'])

/**
 * A radio or checkbox is a field whatever element renders it. Base UI's Radio prevents every
 * Enter itself (it activates on Space only) and its Checkbox cancels the native default, so
 * an Enter already prevented on a choice is not a handler taking the key.
 */
const PILIHAN = new Set(['radio', 'checkbox'])

export function aturanEnter(
  s: SasaranEnter,
  posisi: { indeks: number; jumlah: number },
  satuIsian: boolean,
): AksiEnter {
  const pilihan = PILIHAN.has(s.peran ?? '')
  if ((s.dicegah && !pilihan) || s.pengubah || s.komposisi || s.terbuka) return 'biarkan'
  if (!pilihan) {
    if (s.tag === 'TEXTAREA' || s.tag === 'BUTTON' || s.tag === 'A') return 'biarkan'
    if (s.peran === 'button' || s.peran === 'link' || s.peran === 'option') return 'biarkan'
    if (s.tag === 'INPUT' && INPUT_BUKAN_ISIAN.has(s.jenis ?? '')) return 'biarkan'
  }
  if (posisi.indeks < 0) return 'biarkan'
  if (satuIsian || posisi.indeks >= posisi.jumlah - 1) return 'simpan'
  return 'lanjut'
}

/**
 * An Enter the rule leaves alone ('biarkan') that must still be kept from the browser: in a
 * text field the browser's implicit submission would reach the form's onSubmit (React still
 * dispatches the submit `tahanKirimAsli` cancels) and save from a middle field, on
 * Shift+Enter or from a field outside the walk. Not while an input method composes (its
 * Enter commits the text), and not where Enter belongs to the control (a textarea, a button,
 * a link, a choice drawn as a span).
 */
export function cegahKirimAsli(s: SasaranEnter): boolean {
  return s.tag === 'INPUT' && !INPUT_BUKAN_ISIAN.has(s.jenis ?? '') && !s.komposisi
}
